import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  Switch,
  FormControlLabel
} from '@mui/material';
import categorieService from '../../services/categorieService';

const CategoryModal = ({ open, onClose, onSuccess, categorie = null }) => {
  const [formData, setFormData] = useState({
    code: '',
    libelle: '',
    statut: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (categorie) {
      setFormData({
        code: categorie.code || '',
        libelle: categorie.libelle || '',
        statut: categorie.statut !== undefined ? categorie.statut : true
      });
    } else {
      setFormData({
        code: '',
        libelle: '',
        statut: true
      });
    }
    setError('');
  }, [categorie, open]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.code.trim()) {
      setError('Le code est obligatoire');
      return false;
    }
    if (!formData.libelle.trim()) {
      setError('Le libellé est obligatoire');
      return false;
    }
    if (formData.code.length < 2) {
      setError('Le code doit contenir au moins 2 caractères');
      return false;
    }
    if (formData.libelle.length < 2) {
      setError('Le libellé doit contenir au moins 2 caractères');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const categorieData = {
        code: formData.code.trim().toUpperCase(),
        libelle: formData.libelle.trim(),
        statut: formData.statut
      };

      if (categorie) {
        // Modification
        await categorieService.updateCategory(categorie.id, categorieData);
        onSuccess('Catégorie modifiée avec succès');
      } else {
        // Création
        await categorieService.createCategory(categorieData);
        onSuccess('Catégorie créée avec succès');
      }
      
      onClose();
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError(
        err.response?.data?.message || 
        'Erreur lors de la sauvegarde de la catégorie'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          {categorie ? 'Modifier la catégorie' : 'Ajouter une catégorie'}
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Code de la catégorie"
              value={formData.code}
              onChange={handleChange('code')}
              fullWidth
              required
              disabled={loading}
              placeholder="Ex: ELECTRONICS"
              helperText="Code unique pour identifier la catégorie (sera converti en majuscules)"
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />

            <TextField
              label="Libellé de la catégorie"
              value={formData.libelle}
              onChange={handleChange('libelle')}
              fullWidth
              required
              disabled={loading}
              placeholder="Ex: Électronique"
              helperText="Nom affiché de la catégorie"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.statut}
                  onChange={handleChange('statut')}
                  disabled={loading}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    Catégorie active
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.statut 
                      ? 'La catégorie sera visible et utilisable' 
                      : 'La catégorie sera masquée et non utilisable'
                    }
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            variant="outlined"
          >
            Annuler
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? 'Sauvegarde...' : (categorie ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CategoryModal;
